import { useMemo } from "react";
import { create } from "qrcode";
import { BRAND_MARK_ASPECT, BrandMark } from "@/components/shell/brand-logo";

/**
 * QR code for a URL or short text, drawn as one SVG path so it stays sharp at
 * any size and follows the theme's ink colour. Encoding is done entirely
 * client-side by the `qrcode` package this app already ships — there is no
 * network call and no third-party QR service. The 4-module quiet zone scanners
 * need is part of the drawing.
 *
 * `emblem` puts the Alloy mark in the centre. The code is then encoded at
 * error-correction level H (recovers ~30% damage) and the modules under the
 * mark are cleared, keeping the covered area well inside that budget.
 */
const QUIET = 4;

export function QrCode({
  value,
  size = 160,
  label,
  emblem = false,
  className,
}: {
  value: string;
  size?: number;
  /** Accessible name, e.g. "QR code to download Alloy Signer for Android". */
  label: string;
  emblem?: boolean;
  className?: string;
}) {
  // Encode once per input. `create` throws on an empty value (for example while a pairing
  // code is still loading) or one too long for the chosen error-correction level; render a
  // same-size placeholder then instead of unmounting the page.
  const drawing = useMemo(() => {
    if (!value) return null;
    try {
      const modules = create(value, { errorCorrectionLevel: emblem ? "H" : "M" }).modules;
      const inner = modules.size;
      const n = inner + QUIET * 2;
      // Centre pad: ~22% of the symbol's width, odd-sized so it sits on the grid centre.
      let pad = Math.round(inner * 0.22);
      if (pad % 2 === 0) pad += 1;
      const padStart = (n - pad) / 2;
      const inPad = (x: number, y: number) =>
        emblem && x >= padStart && x < padStart + pad && y >= padStart && y < padStart + pad;
      let d = "";
      for (let y = 0; y < inner; y++) {
        for (let x = 0; x < inner; x++) {
          const gx = x + QUIET;
          const gy = y + QUIET;
          if (modules.data[y * inner + x] && !inPad(gx, gy)) d += `M${gx} ${gy}h1v1h-1z`;
        }
      }
      return { n, pad, padStart, d };
    } catch {
      return null;
    }
  }, [value, emblem]);

  if (!drawing) {
    return (
      <div
        role="img"
        aria-label={label}
        style={{ width: size, height: size }}
        className={["rounded-md border border-dashed border-line bg-surface-muted", className].filter(Boolean).join(" ")}
      />
    );
  }
  const { n, pad, padStart, d } = drawing;

  const mark = pad - 1; // half a module of white around the mark on each side
  // The knockout pad is square but the mark is 1.358:1, so at width `mark` it
  // is much shorter than the pad and has to be centred in it vertically.
  const markTop = padStart + (pad - mark / BRAND_MARK_ASPECT) / 2;
  return (
    <svg
      role="img"
      aria-label={label}
      width={size}
      height={size}
      viewBox={`0 0 ${n} ${n}`}
      shapeRendering="crispEdges"
      className={className}
    >
      <rect width={n} height={n} fill="#fff" />
      <path d={d} fill="var(--ads-ink)" />
      {emblem && (
        <g
          // X keeps the half-module nudge: the mark is exactly one module
          // narrower than the pad, so the nudge lands the ink on module
          // boundaries and the cleared ring around it stays crisp. Y takes no
          // nudge — the mark's height leaves a non-integer margin, so the
          // nudge would push the emblem half a module off centre.
          transform={`translate(${padStart + 0.5} ${markTop})`}
          shapeRendering="geometricPrecision"
        >
          <BrandMark size={mark} />
        </g>
      )}
    </svg>
  );
}
